import { Fragment, useRef, useState } from 'react';
import { Alert, View } from 'react-native';
import { WebView } from 'react-native-webview';
import { useDispatch, useSelector } from 'react-redux';
import { CommonActions, StackActions } from '@react-navigation/native';
import { Layout, Text } from '@ui-kitten/components';
import lodashGet from 'lodash/get';
import lodashIsEmpty from 'lodash/isEmpty';

import Button from '../../Components/Button';
import Header from '../../Components/Header';
import ScreenWrapper from '../../Components/ScreenWrapper';
import modals from '../../Components/Sheets/modals';
import { MODALPROMPT } from '../../Components/Web/Modal/ModalPrompt/config';
import useModalPrompt from '../../Components/Web/Modal/ModalPrompt/hooks/useModalPrompt';
import PopupModal from '../../Components/Web/PopupModal';
import config from '../../Config';
import constants from '../../Config/constants';
import messages from '../../Config/messages';
import { isValidJson } from '../../Helper';
import { getCheckoutRedirectionDelay } from '../../Helper/RemoteConfig';
import useSetSpecificUserData from '../../Hooks/useSetSpecificUserData';
import useToast from '../../Hooks/useToast';
import withCheckout from '../../Hooks/withCheckout';
import routeList from '../../Routes/list';
import { updateCheckoutDetails } from '../../RTK/checkout';
import { checkout } from '../../RTK/defaultValues';
import { isLoggedInSelector } from '../../RTK/user/selectors';
import { setChooseDifferentMethod } from '../../RTK/utility';
import ThemeStyle from '../../Theme/styles';
import { getEnvValue } from '../../Helper/env';

// use this variable on webview like this injectedJavaScript={debug} to console the console log on webview
// to terminal, make sure to add onMessage bcs console on web will show there
const debug = `
  console = new Object();
  console.log = function(...args) {
    window.ReactNativeWebView.postMessage(args.join(', '))
  };
  console.debug = console.log;
  console.info = console.log;
  console.warn = console.log;
  console.error = console.log;
`;

function CheckoutWebview({
  navigation,
  route,
  clearCartAndCheckoutData,
  clearMealPlanAndCheckoutData,
}) {
  const RAZZLE_CHECKOUT_PAYMENT_URL = getEnvValue(
    'RAZZLE_CHECKOUT_PAYMENT_URL',
    { nativeKey: 'CHECKOUT_PAYMENT_URL' },
  );
  const dispatch = useDispatch();
  const webViewRef = useRef();
  const isOrderSuccess = useRef();
  const paySuccessDelayTimeout = useRef();
  const toast = useToast();
  const { showModalPrompt } = useModalPrompt();
  const { updateLastUsedAddress } = useSetSpecificUserData();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const [isPaymentExpired, setIsPaymentExpired] = useState(false); // for web modal use

  const params = lodashGet(route, 'params') || {};
  const {
    isMealPlan,
    payment,
    storeId,
    url,
    waitSuccess = false,
    // this 2 use when going back from checkout webview (e.g: user click back, return to checkout button after payment failed etc.)
    // TIPS: no need to pass this 2 props if you just want to go back
    backRoute,
    backParams,
    successRoute,
  } = params;

  const isReservationCheckout = successRoute?.tab === routeList.RESERVATION_TAB;

  const _goBack = () => {
    if (constants.isWeb) {
      setIsPaymentExpired(false); // close modal first before redirect
    }
    navigation.goBack(); // this close the checkout webview
    if (backRoute) {
      // if has backRoute,  navigate to it
      navigation.navigate(backRoute, backParams);
    }
  };

  const _successOrder = (orderId) => {
    clearTimeout(paySuccessDelayTimeout.current);
    paySuccessDelayTimeout.current = null;
    isOrderSuccess.current = true;
    // clearing of cart and updating of last used address
    if (isMealPlan) {
      clearMealPlanAndCheckoutData();
    } else {
      clearCartAndCheckoutData(); // clear cart if not meal plan
    }
    updateLastUsedAddress(); // update last used address
    if (!lodashIsEmpty(successRoute)) {
      toast.show(
        messages.ORDER_SUCCESS,
        true,
        isReservationCheckout ? 'info' : undefined,
      );
      _clearNavigatorAndNavigate({
        tabRoute: successRoute.tab,
        screenRoute: successRoute.screen,
        screenParams: { id: orderId, ...successRoute.params },
        isDeep: true,
      });
    } else {
      // show toast and redirect to order page
      toast.show(messages.ORDER_SUCCESS);
      _clearNavigatorAndNavigate({
        tabRoute: routeList.BASKET_TAB,
        screenRoute: routeList.BASKET,
        screenParams: {
          activeTab: constants.isWeb ? 0 : 1,
          highlightOrderId: 'first',
        },
      });
    }
  };

  const _clearNavigatorAndNavigate = ({
    tabRoute,
    screenRoute,
    screenParams,
    isDeep,
  }) => {
    if (constants.isWeb) {
      window.history.replaceState('', '', routeList.HOME);
      navigation.dispatch(
        StackActions.replace(routeList.HOME, {
          screen: 'MainMenu',
          params: isDeep
            ? {
                screen: tabRoute, // navigate to tabRoute
                params: {
                  screen: screenRoute, // load screenRoute page
                  params: screenParams, // with this parameters
                },
              }
            : {
                screen: tabRoute, // navigate to tabRoute
                params: screenParams, // with this parameters
              },
        }),
      );
    } else if (isLoggedIn) {
      // reset for logged in user
      navigation.dispatch(
        // clear all stack navigation
        CommonActions.reset({
          index: 0,
          routes: [
            {
              name: routeList.ROOT_TAB, // reset it and set root tab the one on navigation
              state: {
                routes: [
                  {
                    name: tabRoute, // navigate to tabRoute
                    params: {
                      screen: screenRoute, // load screenRoute page
                      params: screenParams, // with this parameters
                    },
                  },
                ],
              },
            },
          ],
        }),
      );
    } else {
      // reset for guest checkout
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [
            {
              name: screenRoute, // the target screen name
              params: screenParams, // optional params
            },
          ],
        }),
      );
    }
  };

  const _listenToMessage = async ({ nativeEvent: { data, origin } }) => {
    const eventName = isValidJson(data) ? JSON.parse(data).message : data;
    const checkOriginWeb = constants.isWeb ? origin === config.PAYMENT : true;

    console.log(
      'Received message from WebView:',
      eventName,
      'with data:',
      data,
      'and origin:',
      origin,
    );

    console.log('Check out app URL:', config.PAYMENT);
    console.log('constants.isWeb:', constants.isWeb);
    console.log('RAZZLE_CHECKOUT_PAYMENT_URL', RAZZLE_CHECKOUT_PAYMENT_URL);
    console.log(
      'RAZZLE_CHECKOUT_PAYMENT_URL via helper',
      RAZZLE_CHECKOUT_PAYMENT_URL,
    );

    if (
      eventName === 'payment-verify' &&
      !paySuccessDelayTimeout.current &&
      !isOrderSuccess.current &&
      !waitSuccess
    ) {
      // payment success on pickup backend, wait 5s before redirect to order page
      // payment is verified, safe to go back to order page
      paySuccessDelayTimeout.current = setTimeout(
        () => _successOrder(isValidJson(data) ? JSON.parse(data).order_id : ''),
        getCheckoutRedirectionDelay() * 1000,
      );
    } else if (eventName === 'send-attributes' && checkOriginWeb) {
      // card info is needed, pass the card information
      webViewRef?.current?.postMessage(
        JSON.stringify({
          message: 'payment-attributes',
          attributes: {
            card_details: payment.cardNumber.replace(/\s/g, ''),
            exp_month: payment.cardMonth,
            exp_year: payment.cardYear,
            cvc: payment.cardCvv,
          },
        }),
        constants.isWeb ? config.PAYMENT : undefined,
      );
    } else if (eventName === 'payment-succeeded') {
      // payment success, go back to order page
      _successOrder(isValidJson(data) ? JSON.parse(data).order_id : '');
    } else if (eventName === 'payment-expired') {
      // payment is expired, payment is taking too long
      if (constants.isWeb) {
        setIsPaymentExpired(true);
      } else {
        Alert.alert(
          messages.PAYMENT_EXPIRED.title,
          messages.PAYMENT_EXPIRED.message,
          [{ text: 'OK', onPress: _goBack }],
        );
      }
    } else if (eventName === 'payment-failed') {
      // remove continue to payment from cart, so user can select new payment method on checkout page
      dispatch(
        updateCheckoutDetails({
          store_id: storeId,
          keyToUpdate: checkout.keys.CONTINUE_TO_PAYMENT,
          keyValue: undefined,
        }),
      );
      // go back to previous screen
      _goBack();
      // show prompt
      const modalPayload = {
        title: 'Payment Failed',
        message:
          'The card has been declined by the issuing bank. Please contact them for more information.',
        buttonStatus: isReservationCheckout ? 'info' : 'success',
        buttonText: 'Change Payment Method',
      };
      if (constants.isWeb) {
        // need delay on web, modal not gonna show if no delay
        setTimeout(async () => {
          const result = await showModalPrompt(
            MODALPROMPT.prompt,
            modalPayload,
          );
          if (result?.buttonClicked) {
            dispatch(setChooseDifferentMethod(true));
          }
        }, 200);
      } else {
        const result = await modals.show(modals.PROMPT, modalPayload);
        if (result?.buttonClicked) {
          dispatch(setChooseDifferentMethod(true));
        }
      }
    }
  };

  return (
    <Fragment>
      {constants.isWeb && (
        <Fragment>
          <Header plain={true} store_id={storeId} backButtonText="Back" />
          <PopupModal
            isOpen={isPaymentExpired}
            onClosed={_goBack}
            title={messages.PAYMENT_EXPIRED.title}
            hasHeaderLine
          >
            <Layout>
              <Text style={[ThemeStyle.textCenter, ThemeStyle.spacingBottom]}>
                {messages.PAYMENT_EXPIRED.message}
              </Text>
              <View style={ThemeStyle.flexDirectionRowCenterCenter}>
                <Button onPress={_goBack} status="success" size="small">
                  {() => (
                    <Text
                      appearance="alternative"
                      category="c1"
                      style={ThemeStyle.pageHorizontalSpacing}
                    >
                      OK
                    </Text>
                  )}
                </Button>
              </View>
            </Layout>
          </PopupModal>
        </Fragment>
      )}
      <ScreenWrapper
        header={
          !constants.isWeb && (
            <Header title="Checkout" alignTitle="left" withBorder />
          )
        }
        scroll={false}
        withFooter={false}
        fullWidth={constants.isWeb}
      >
        <WebView
          ref={webViewRef}
          source={{ uri: url }}
          bounces={false}
          onMessage={_listenToMessage}
          // comment for now the originWhitelist, because it open browser instead of loading it on webview
          // even tho the the origin and config.PAYMENT is same
          // originWhitelist={[config.PAYMENT]}
        />
      </ScreenWrapper>
    </Fragment>
  );
}

export default withCheckout(CheckoutWebview);
